import {
  Button,
  CustomAlertDialog,
  ErrorMessage,
  PageContainer,
  Spinner,
} from "@/components";
import SearchInput from "@/components/Search";
import SkillDialog from "@/components/formDialog/SkillDialog";
import {
  useDeleteSkillMutation,
  useSkillsQuery,
} from "@/hooks/api/profile.queries";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import { cn } from "@/lib/utils";
import { SkillType } from "@/services/profile.service";
import {
  BookOpen,
  Calendar,
  ChevronRight,
  Edit,
  MoreHorizontal,
  Plus,
  Search,
  Sparkles,
  Target,
  Trash2,
  Trophy,
} from "lucide-react";
import { useCallback, useState, useMemo } from "react";
import {
  ActionDropdown,
  ActionDropdownContent,
  ActionDropdownTrigger,
} from "@/components/ActionDropdown";

export default function Skills() {
  const { data: skills, isLoading, isError, refetch } = useSkillsQuery();
  const deleteMutation = useDeleteSkillMutation();
  const [searchQuery, setSearchQuery] = useState("");

  const deleteDialog = useAlertDialog();
  const skillDialog = useFormDialog({
    mode: "create" as "create" | "edit",
    skillId: "",
    skillData: null as SkillType | null,
  });

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  const handleAction = (action: "edit" | "delete", skill: SkillType) => {
    if (action === "delete") {
      deleteDialog.open({
        message: (
          <span>
            Are you sure you want to delete the skill{" "}
            <strong className="text-gray-800">{skill.name}</strong>?
          </span>
        ),
        mantraId: skill.id,
        mantraTitle: skill.name,
        action: "delete",
      });
    } else {
      skillDialog.open({
        mode: "edit",
        skillId: skill.id,
        skillData: skill,
      });
    }
  };

  const filteredSkills = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return (skills || []).filter((s) =>
      s.name.toLowerCase().includes(query)
    );
  }, [skills, searchQuery]);

  const stats = useMemo(() => {
    const list = skills || [];
    const expertCount = list.filter((s) => s.proficiency?.toLowerCase() === "expert").length;
    const totalExp = list.reduce((acc, s) => acc + (s.years_of_experience || 0), 0);
    return {
      total: list.length,
      expert: expertCount,
      avgExp: list.length ? (totalExp / list.length).toFixed(1) : 0,
    };
  }, [skills]);

  if (isError) {
    return <ErrorMessage message="Failed to load skills." onRetry={() => refetch()} />;
  }

  return (
    <PageContainer className="pb-10">
      {/* ─── Hero Section ─── */}
      <div className="relative mb-8 overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-700 to-purple-800 p-8 text-white shadow-2xl">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 h-64 w-64 rounded-full bg-indigo-500/20 blur-3xl" />

        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="text-yellow-300 h-5 w-5" />
              <span className="text-indigo-100 text-xs font-bold uppercase tracking-widest">Skill Mastery</span>
            </div>
            <h1 className="text-4xl font-black tracking-tight">Professional Portfolio</h1>
            <p className="text-indigo-100/80 text-sm max-w-md">
              Manage and showcase your technical expertise. Your skills define your career path.
            </p>
          </div>
          <Button
            onClick={() => skillDialog.open({ mode: "create", skillId: "", skillData: null })}
            className="h-14 px-8 bg-white text-indigo-700 hover:bg-indigo-50 font-bold rounded-2xl shadow-xl shadow-indigo-900/20 group transition-all duration-300"
          >
            <Plus className="mr-2 h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
            Add New Skill
          </Button>
        </div>

        {/* ─── Quick Stats ─── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg"><BookOpen size={20} /></div>
              <div>
                <p className="text-[10px] font-bold text-indigo-200 uppercase tracking-wider">Total Skills</p>
                <p className="text-2xl font-black">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg"><Trophy size={20} className="text-amber-300" /></div>
              <div>
                <p className="text-[10px] font-bold text-indigo-200 uppercase tracking-wider">Expert Level</p>
                <p className="text-2xl font-black text-amber-300">{stats.expert}</p>
              </div>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg"><Target size={20} className="text-emerald-300" /></div>
              <div>
                <p className="text-[10px] font-bold text-indigo-200 uppercase tracking-wider">Avg. Experience</p>
                <p className="text-2xl font-black tracking-tight">{stats.avgExp} <span className="text-xs font-medium">years</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Search & Controls ─── */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
        <div className="w-full md:w-96">
          <SearchInput
            placeholder="Search across your skills..."
            value={searchQuery}
            onValueChange={setSearchQuery}
            className={{
              inputGroupClass: "bg-white border border-gray-100 rounded-2xl transition-all shadow-sm focus-within:ring-4 focus-within:ring-indigo-50 focus-within:border-indigo-200 h-[46px]",
              inputClass: "bg-transparent text-sm font-normal focus-visible:ring-0",
            }}
          />
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400 font-semibold uppercase tracking-wider">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          Updated {new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
        </div>
      </div>

      {/* ─── Skills Grid ─── */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <Spinner />
          <p className="text-gray-400 text-sm animate-pulse">Organizing your toolkit...</p>
        </div>
      ) : filteredSkills.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 bg-gray-50/50 rounded-3xl border border-dashed border-gray-200 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 text-gray-400">
            <Search size={28} />
          </div>
          <h3 className="text-lg font-bold text-gray-700">No skills matching "{searchQuery}"</h3>
          <p className="text-gray-400 text-sm mt-1">Try a different search term or add a new skill.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredSkills.map((skill) => (
            <SkillCard key={skill.id} skill={skill} onAction={handleAction} />
          ))}
        </div>
      )}

      {/* Add / Edit Dialog */}
      <SkillDialog
        mode={skillDialog.state.mode}
        isOpen={skillDialog.state.isOpen}
        onOpenChange={(open) => !open && skillDialog.close()}
        skillId={skillDialog.state.skillId}
        skillData={skillDialog.state.skillData}
        onCancel={skillDialog.close}
      />

      {/* Delete Confirmation */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Remove Skill"
        description={deleteDialog.state.message}
        confirmText="Remove Achievement"
        cancelText="Keep Skill"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
}

function SkillCard({ skill, onAction }: { skill: SkillType; onAction: (a: "edit" | "delete", s: SkillType) => void }) {
  const prof = skill.proficiency?.toLowerCase() || "beginner";

  const getProficiencyStyle = () => {
    switch (prof) {
      case "expert": return "bg-amber-50 text-amber-700 border-amber-200";
      case "advanced": return "bg-purple-50 text-purple-700 border-purple-200";
      case "intermediate": return "bg-blue-50 text-blue-700 border-blue-200";
      default: return "bg-slate-50 text-slate-600 border-slate-200";
    }
  };

  const getProgress = () => {
    switch (prof) {
      case "expert": return "w-full bg-amber-400";
      case "advanced": return "w-3/4 bg-purple-500";
      case "intermediate": return "w-1/2 bg-blue-500";
      default: return "w-1/4 bg-slate-400";
    }
  };

  return (
    <div className="group relative bg-white rounded-2xl border border-gray-100 p-6 hover:border-indigo-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 flex items-center justify-center text-indigo-600 font-bold text-lg">
          {skill.name.charAt(0).toUpperCase()}
        </div>
        <ActionDropdown>
          <ActionDropdownTrigger
            triggerContent={<MoreHorizontal className="h-4 w-4" />}
            buttonVariant="ghost"
            className={{ buttonClass: "h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity" }}
          />
          <ActionDropdownContent
            align="end"
            className="rounded-xl border-gray-100 shadow-xl"
            dropdownShape={[
              {
                type: "item",
                label: "Edit Skill",
                icon: <Edit size={14} className="text-gray-400" />,
                className: "gap-2 cursor-pointer",
                onSelect: () => onAction("edit", skill),
              },
              {
                type: "item",
                label: "Remove",
                icon: <Trash2 size={14} className="text-red-500" />,
                className: "gap-2 cursor-pointer text-red-600 focus:text-red-700",
                onSelect: () => onAction("delete", skill),
              },
            ]}
          />
        </ActionDropdown>
      </div>

      <h3 className="text-lg font-bold text-gray-900 group-hover:text-indigo-600 transition-colors mb-1 truncate">{skill.name}</h3>

      <div className="flex items-center gap-2 mb-4">
        <span className={cn("px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border", getProficiencyStyle())}>
          {skill.proficiency}
        </span>
        {skill.years_of_experience > 0 && (
          <span className="flex items-center gap-1 text-[10px] font-bold text-gray-400 uppercase">
            <Calendar size={10} /> {skill.years_of_experience} {skill.years_of_experience === 1 ? 'Year' : 'Years'}
          </span>
        )}
      </div>

      <div className="space-y-1.5 pt-2">
        <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-widest text-gray-400">
          <span>Mastery</span>
          <span className="text-gray-600">{prof === 'expert' ? '100%' : prof === 'advanced' ? '75%' : prof === 'intermediate' ? '50%' : '25%'}</span>
        </div>
        <div className="h-1.5 w-full bg-gray-50 rounded-full overflow-hidden">
          <div className={cn("h-full transition-all duration-500 ease-out", getProgress())} />
        </div>
      </div>

      <div className="absolute bottom-4 right-4 text-indigo-100 opacity-0 group-hover:opacity-100 transition-opacity">
        <ChevronRight size={20} />
      </div>
    </div>
  );
}
